import { useEffect, useState } from 'react';
import { clienteService, pagoService, prestamoService } from '../../services/api';
import '../../styles/Pagos.css';

const PlanPagos = ({ prestamoIdInicial }) => {
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamos, setPrestamos] = useState([]);
  const [prestamoSeleccionado, setPrestamoSeleccionado] = useState(null);
  const [busquedaId, setBusquedaId] = useState('');
  const [planAmortizacion, setPlanAmortizacion] = useState([]);
  const [pagosRegistrados, setPagosRegistrados] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteObj, setClienteObj] = useState(null);
  // Estados para edición de fecha de pago
  const [editandoCuota, setEditandoCuota] = useState(null);
  const [fechaEditando, setFechaEditando] = useState('');

  const formatearFechaLocal = (fechaISO) => {
    if (!fechaISO) return null;
    const fechaStr = fechaISO.split('T')[0];
    const [year, month, day] = fechaStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
  };

  const formatearMonto = (valor) => {
    return Number.parseFloat(valor || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const clampDay = (year, month, day) => {
    const ultimoDia = new Date(year, month + 1, 0).getDate();
    if (day < 1) return 1;
    if (day > ultimoDia) return ultimoDia;
    return day;
  };

  const addMonthsKeepingDay = (baseDate, monthsToAdd, desiredDay) => {
    const monthIndex = baseDate.getMonth() + monthsToAdd;
    const targetYear = baseDate.getFullYear() + Math.floor(monthIndex / 12);
    const targetMonth = ((monthIndex % 12) + 12) % 12;
    const day = clampDay(targetYear, targetMonth, desiredDay);
    return new Date(targetYear, targetMonth, day);
  };

  const calcularFechaQuincenal = (baseDate, pasos) => {
    const baseDay = baseDate.getDate();
    const steps = Math.max(0, parseInt(pasos, 10) || 0);
    let monthOffset;
    let desiredDay;

    if (baseDay <= 15) {
      monthOffset = Math.floor(steps / 2);
      desiredDay = steps % 2 === 0 ? baseDay : Math.min(baseDay + 15, 30);
    } else {
      if (steps % 2 === 0) {
        monthOffset = Math.floor(steps / 2);
        desiredDay = baseDay;
      } else {
        monthOffset = Math.floor((steps + 1) / 2);
        desiredDay = Math.max(baseDay - 15, 1);
      }
    }

    return addMonthsKeepingDay(baseDate, monthOffset, desiredDay);
  };

  const calcularFechaCuota = (fechaReferencia, modalidad, pasos) => {
    const baseDate = new Date(fechaReferencia.getTime());
    const steps = Math.max(0, parseInt(pasos, 10) || 0);
    const modo = (modalidad || '').toLowerCase();

    if (modo.includes('diario') || modo.includes('diaria')) {
      const resultado = new Date(baseDate);
      resultado.setDate(resultado.getDate() + steps);
      return resultado;
    }

    if (modo.includes('semanal')) {
      const resultado = new Date(baseDate);
      resultado.setDate(resultado.getDate() + (steps * 7));
      return resultado;
    }

    if (modo.includes('quincenal') || modo.includes('quincena')) {
      return calcularFechaQuincenal(baseDate, steps);
    }

    if (modo.includes('mensual')) {
      return addMonthsKeepingDay(baseDate, steps, baseDate.getDate());
    }

    // Modalidad desconocida -> asumir diaria
    const resultado = new Date(baseDate);
    resultado.setDate(resultado.getDate() + steps);
    return resultado;
  };

  const totalesPagos = pagosRegistrados.reduce((acumulado, pago) => {
    acumulado.capital += Number.parseFloat(pago.monto_capital || 0);
    acumulado.interes += Number.parseFloat(pago.monto_interes || 0);
    acumulado.mora += Number.parseFloat(pago.monto_mora || 0);
    acumulado.total += Number.parseFloat(pago.monto_total || 0);
    return acumulado;
  }, { capital: 0, interes: 0, mora: 0, total: 0 });

  useEffect(() => {
    cargarClientes();

    // Cerrar dropdown al hacer clic fuera
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await clienteService.getAll({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  // Filtrar clientes según búsqueda
  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10);

  const seleccionarCliente = async (cliente) => {
    setClienteObj(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
    setClienteSeleccionado(cliente.id);
    setPrestamos([]);
    setPrestamoSeleccionado(null);
    setPlanAmortizacion([]);
    setPagosRegistrados([]);

    try {
      const response = await prestamoService.getAll({ cliente_id: cliente.id });
      setPrestamos(response.data.prestamos || []);
    } catch (error) {
      console.error('Error al cargar préstamos:', error);
    }
  };

  const limpiarCliente = () => {
    setClienteObj(null);
    setClienteSeleccionado('');
    setBusquedaCliente('');
    setPrestamos([]);
    setPrestamoSeleccionado(null);
    setPlanAmortizacion([]);
    setPagosRegistrados([]);
  };

  const handleClienteChange = async (e) => {
    const clienteId = e.target.value;
    setClienteSeleccionado(clienteId);
    setPrestamos([]);
    setPrestamoSeleccionado(null);
    setPlanAmortizacion([]);
    setPagosRegistrados([]);

    if (clienteId) {
      try {
        const response = await prestamoService.getAll({ cliente_id: clienteId });
        setPrestamos(response.data.prestamos || []);
      } catch (error) {
        console.error('Error al cargar préstamos:', error);
      }
    }
  };

  const buscarPrestamoPorId = async (prestamoId) => {
    if (!prestamoId) return;

    try {
      const response = await prestamoService.getById(prestamoId);
      const prestamo = response.data;
      setPrestamoSeleccionado(prestamo);
      generarPlanAmortizacion(prestamo);

      if (prestamo?.cliente) {
        const cliente = prestamo.cliente;
        setClienteObj(cliente);
        setClienteSeleccionado(cliente.id);
        setBusquedaCliente(
          `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim()
        );
      }
    } catch (error) {
      alert('Préstamo no encontrado');
    }
  };

  const handleBuscarPorId = async () => {
    if (!busquedaId) return;
    buscarPrestamoPorId(busquedaId);
  };

  useEffect(() => {
    if (prestamoIdInicial) {
      setBusquedaId(prestamoIdInicial);
      buscarPrestamoPorId(prestamoIdInicial);
    }
  }, [prestamoIdInicial]);

  const handleSeleccionarPrestamo = (prestamo) => {
    setPrestamoSeleccionado(prestamo);
    generarPlanAmortizacion(prestamo);
  };

  const generarPlanAmortizacion = async (prestamo) => {
    const plan = [];
    const montoCuota = prestamo.total / prestamo.cuotas;
    const capitalCuota = prestamo.capital / prestamo.cuotas;
    const interesCuota = prestamo.interes / prestamo.cuotas;

    let saldoCapital = prestamo.capital;
    let saldoInteres = prestamo.interes;
    let saldoTotal = prestamo.total;

    // Convertir fecha_efectividad sin problema de zona horaria
    const fechaEfectividadStr = prestamo.fecha_efectividad.split('T')[0];
    const [yearEfec, monthEfec, dayEfec] = fechaEfectividadStr.split('-');
    const fechaInicio = new Date(parseInt(yearEfec), parseInt(monthEfec) - 1, parseInt(dayEfec));

    // Obtener pagos reales del backend
    let pagosReales = [];
    try {
      const response = await pagoService.getAll({ 
        prestamo_id: prestamo.id,
        per_page: 1000  // Obtener todos los pagos sin límite
      });
      pagosReales = response.data.pagos || [];
      // Ordenar pagos por ID ascendente para asegurar el orden correcto
      pagosReales.sort((a, b) => a.id - b.id);
      console.log('📋 Pagos cargados para préstamo', prestamo.id, ':', pagosReales.length);
    } catch (error) {
      console.error('Error al cargar pagos:', error);
    }

    setPagosRegistrados(pagosReales);

    for (let i = 1; i <= prestamo.cuotas; i++) {
      const fechaCuota = calcularFechaCuota(fechaInicio, prestamo.modalidad, i - 1);

      let capitalPagado = 0;
      let interesPagado = 0;
      let moraPagada = 0;
      let totalPagado = 0;
      let fechaPagoReal = null;
      let pagoId = null;
      let multiplePagos = false;
      let cantidadPagos = 0;

      if (prestamo.cuotas === 1) {
        cantidadPagos = pagosReales.length;
        multiplePagos = cantidadPagos > 1;
        pagosReales.forEach((pago) => {
          capitalPagado += parseFloat(pago.monto_capital || 0);
          interesPagado += parseFloat(pago.monto_interes || 0);
          moraPagada += parseFloat(pago.monto_mora || 0);
          totalPagado += parseFloat(pago.monto_total || 0);
          if (pago.fecha_pago) {
            const fechaFormateada = formatearFechaLocal(pago.fecha_pago);
            if (fechaFormateada) {
              fechaPagoReal = fechaFormateada;
            }
          }
        });
        if (!multiplePagos && pagosReales[0]) {
          pagoId = pagosReales[0].id;
        }
      } else {
        const pagoReal = pagosReales[i - 1];
        if (pagoReal) {
          cantidadPagos = 1;
          pagoId = pagoReal.id;
          capitalPagado = parseFloat(pagoReal.monto_capital || 0);
          interesPagado = parseFloat(pagoReal.monto_interes || 0);
          moraPagada = parseFloat(pagoReal.monto_mora || 0);
          totalPagado = parseFloat(pagoReal.monto_total || 0);
          fechaPagoReal = formatearFechaLocal(pagoReal.fecha_pago) || null;
        }
      }

      const pagado = prestamo.cuotas === 1
        ? totalPagado >= (montoCuota - 0.01)
        : i <= prestamo.cuotas_pagadas;

      console.log(`Cuota ${i}: pagoId=${pagoId}, pagos=${cantidadPagos}, fecha=${fechaPagoReal}`);

      plan.push({
        numero: i,
        fecha: fechaCuota.toLocaleDateString(),
        fechaPago: fechaPagoReal, // Nueva columna: fecha real del pago
        pagoId: pagoId, // ID del pago para poder editarlo
        multiplePagos,
        cantidadPagos,
        capital: capitalCuota,
        interes: interesCuota,
        cuota: montoCuota,
        // Montos reales del pago
        capitalPagado: capitalPagado,
        interesPagado: interesPagado,
        moraPagada: moraPagada,
        totalPagado: totalPagado,
        saldoCapital: saldoCapital - capitalCuota,
        saldoInteres: saldoInteres - interesCuota,
        saldoTotal: saldoTotal - montoCuota,
        pagado: pagado
      });

      saldoCapital -= capitalCuota;
      saldoInteres -= interesCuota;
      saldoTotal -= montoCuota;
    }

    setPlanAmortizacion(plan);
  };

  const imprimirPlan = () => {
    window.print();
  };

  const exportarPDF = () => {
    // Esta función se puede mejorar con una librería como jsPDF
    alert('Funcionalidad de exportar a PDF - Por implementar con jsPDF');
  };

  const handleClickFechaPago = (cuota) => {
    if (cuota.multiplePagos) {
      alert('❌ Esta cuota tiene varios pagos registrados. Revisa la sección de "Pagos Registrados" para ver los detalles y ajusta cada pago desde el módulo de pagos si es necesario.');
      return;
    }
    if (!cuota.pagoId) {
      alert('❌ Esta cuota no tiene un pago registrado aún');
      return;
    }
    setEditandoCuota(cuota.numero);
    // Convertir la fecha a formato YYYY-MM-DD para el input
    if (cuota.fechaPago) {
      const partes = cuota.fechaPago.split('/');
      if (partes.length === 3) {
        const fechaISO = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
        setFechaEditando(fechaISO);
      }
    }
  };

  const handleGuardarFecha = async (cuota) => {
    if (!fechaEditando) {
      alert('❌ Debe seleccionar una fecha');
      return;
    }

    try {
      await pagoService.updateFechaPago(cuota.pagoId, { fecha_pago: fechaEditando });
      alert('✅ Fecha de pago actualizada correctamente');
      // Recargar el plan de amortización
      generarPlanAmortizacion(prestamoSeleccionado);
      setEditandoCuota(null);
      setFechaEditando('');
    } catch (error) {
      alert('❌ Error al actualizar fecha: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelarEdicion = () => {
    setEditandoCuota(null);
    setFechaEditando('');
  };

  return (
        <div className="plan-pagos-container">
      <div className="plan-pagos-header">
        <h2>Reporte de Pago x Cliente (Tabla de Amortización)</h2>

        <div className="busqueda-section">
          <div className="form-group">
            <label>Buscar por Cliente:</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, cédula, apodo o ID..."
                value={busquedaCliente}
                onChange={(e) => {
                  setBusquedaCliente(e.target.value);
                  setMostrarDropdown(true);
                }}
                onFocus={() => setMostrarDropdown(true)}
              />
              {clienteObj && (
                <button
                  type="button"
                  onClick={limpiarCliente}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6c757d'
                  }}
                >
                  ×
                </button>
              )}

              {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {clientesFiltrados.map((cliente) => (
                    <div
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      {cliente.foto_url ? (
                        <img
                          src={cliente.foto_url}
                          alt={cliente.nombres}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#007bff',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {cliente.nombres} {cliente.apellidos}
                          {cliente.apodo && (
                            <span style={{
                              marginLeft: '8px',
                              color: '#007bff',
                              fontStyle: 'italic',
                              fontWeight: 'normal'
                            }}>
                              ({cliente.apodo})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          Cédula: {cliente.cedula} • ID: {cliente.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="separador">O</div>

          <div className="busqueda-id">
            <label>Buscar por ID de Préstamo:</label>
            <div className="input-group">
              <input
                type="text"
                value={busquedaId}
                onChange={(e) => setBusquedaId(e.target.value)}
                placeholder="ID del préstamo"
              />
              <button type="button" onClick={handleBuscarPorId}>Buscar</button>
            </div>
          </div>
        </div>

        {prestamos.length > 0 && (
          <div className="prestamos-list">
            <h3>Préstamos del Cliente</h3>
            {prestamos.map(prestamo => (
              <div
                key={prestamo.id}
                className={`prestamo-item ${prestamoSeleccionado?.id === prestamo.id ? 'selected' : ''}`}
                onClick={() => handleSeleccionarPrestamo(prestamo)}
              >
                <div><strong>ID:</strong> {prestamo.id}</div>
                <div><strong>Total:</strong> ${prestamo.total.toFixed(2)}</div>
                <div><strong>Cuotas:</strong> {prestamo.cuotas}</div>
                <div><strong>Modalidad:</strong> {prestamo.modalidad}</div>
                <div><strong>Estado:</strong> {prestamo.estado}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {prestamoSeleccionado && planAmortizacion.length > 0 && (
        <div className="plan-amortizacion">
          <div className="plan-header">
            <h1>REPORTE DE PAGO X CLIENTE - TABLA DE AMORTIZACIÓN</h1>
            <p className="plan-numero">
              Préstamo No. {prestamoSeleccionado.id} ·{' '}
              {prestamoSeleccionado.cliente?.nombres
                ? `${prestamoSeleccionado.cliente.nombres} ${prestamoSeleccionado.cliente.apellidos || ''}`.trim()
                : 'Cliente sin nombre'}
            </p>
          </div>

          <div className="plan-info">
            <div className="info-section">
              <h3>Información del Préstamo</h3>
              <div className="info-grid">
                <div><strong>Monto Prestado:</strong> ${prestamoSeleccionado.monto_prestado.toFixed(2)}</div>
                <div><strong>Capital:</strong> ${prestamoSeleccionado.capital.toFixed(2)}</div>
                <div><strong>Interés:</strong> ${prestamoSeleccionado.interes.toFixed(2)}</div>
                <div><strong>Total a Pagar:</strong> ${prestamoSeleccionado.total.toFixed(2)}</div>
                <div><strong>Tasa:</strong> {prestamoSeleccionado.tasa}%</div>
                <div><strong>Cuotas:</strong> {prestamoSeleccionado.cuotas}</div>
                <div><strong>Modalidad:</strong> {prestamoSeleccionado.modalidad}</div>
                <div><strong>Tipo:</strong> {prestamoSeleccionado.tipo}</div>
                <div><strong>Fecha de Desembolso:</strong> {(() => {
                  const fechaStr = prestamoSeleccionado.fecha_inicio.split('T')[0];
                  const [year, month, day] = fechaStr.split('-');
                  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
                })()} 📤</div>
                <div><strong>Fecha del Primer Pago:</strong> {(() => {
                  const fechaStr = prestamoSeleccionado.fecha_efectividad.split('T')[0];
                  const [year, month, day] = fechaStr.split('-');
                  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
                })()} 📥</div>
              </div>
            </div>
          </div>

          <div className="tabla-wrapper">
            <table className="tabla-amortizacion">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Fecha</th>
                  <th>Fecha Pago</th>
                  <th style={{background: '#e8f5e9'}}>Capital Pagado</th>
                  <th style={{background: '#fff3e0'}}>Interés Pagado</th>
                  <th style={{background: '#ffebee'}}>Mora Pagada</th>
                  <th style={{background: '#e3f2fd'}}>Total Pagado</th>
                  <th>Saldo Capital</th>
                  <th>Saldo Interés</th>
                  <th>Saldo Total</th>
                  <th className="no-print">Estado</th>
                </tr>
              </thead>
              <tbody>
                {planAmortizacion.map((cuota) => (
                  <tr key={cuota.numero} className={cuota.pagado ? 'pagado' : ''}>
                    <td>
                      {cuota.numero}
                      {cuota.cantidadPagos > 1 && (
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#555' }}>
                          {cuota.cantidadPagos} pagos registrados
                        </span>
                      )}
                    </td>
                    <td>{cuota.fecha}</td>
                    <td 
                      style={{ 
                        cursor: cuota.pagoId && !cuota.multiplePagos ? 'pointer' : 'default',
                        position: 'relative',
                        padding: editandoCuota === cuota.numero ? '0' : undefined
                      }}
                      onClick={() => editandoCuota !== cuota.numero && handleClickFechaPago(cuota)}
                      title={cuota.multiplePagos
                        ? 'Esta cuota tiene varios pagos registrados'
                        : (cuota.pagoId ? 'Click para editar fecha' : 'No hay pago registrado')}
                    >
                      {editandoCuota === cuota.numero ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem' }}>
                          <input
                            type="date"
                            value={fechaEditando}
                            onChange={(e) => setFechaEditando(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            style={{
                              padding: '0.25rem',
                              fontSize: '0.9rem',
                              border: '2px solid #27ae60',
                              borderRadius: '4px'
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGuardarFecha(cuota);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#27ae60',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelarEdicion();
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                            title="Cancelar"
                          >
                            ✖
                          </button>
                        </div>
                      ) : (
                        <span style={{ 
                          textDecoration: cuota.pagoId ? 'underline' : 'none',
                          textDecorationStyle: 'dotted'
                        }}>
                          {cuota.fechaPago || '-'}
                        </span>
                      )}
                    </td>
                    <td style={{background: cuota.capitalPagado > 0 ? '#e8f5e9' : 'transparent'}}>
                      {cuota.capitalPagado > 0 ? `$${cuota.capitalPagado.toFixed(2)}` : '-'}
                    </td>
                    <td style={{background: cuota.interesPagado > 0 ? '#fff3e0' : 'transparent'}}>
                      {cuota.interesPagado > 0 ? `$${cuota.interesPagado.toFixed(2)}` : '-'}
                    </td>
                    <td style={{background: cuota.moraPagada > 0 ? '#ffebee' : 'transparent'}}>
                      {cuota.moraPagada > 0 ? `$${cuota.moraPagada.toFixed(2)}` : '-'}
                    </td>
                    <td style={{background: cuota.totalPagado > 0 ? '#e3f2fd' : 'transparent', fontWeight: 'bold'}}>
                      {cuota.totalPagado > 0 ? `$${cuota.totalPagado.toFixed(2)}` : '-'}
                    </td>
                    <td>${cuota.saldoCapital.toFixed(2)}</td>
                    <td>${cuota.saldoInteres.toFixed(2)}</td>
                    <td><strong>${cuota.saldoTotal.toFixed(2)}</strong></td>
                    <td className="no-print">
                      {cuota.pagado ? (
                        <span className="estado-pagado">✓ Pagado</span>
                      ) : (
                        <span className="estado-pendiente">Pendiente</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="3"><strong>TOTALES:</strong></td>
                  <td><strong>${prestamoSeleccionado.capital.toFixed(2)}</strong></td>
                  <td><strong>${prestamoSeleccionado.interes.toFixed(2)}</strong></td>
                  <td><strong>${prestamoSeleccionado.total.toFixed(2)}</strong></td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>

        {pagosRegistrados.length > 0 && (
          <div className="tabla-wrapper no-print" style={{ marginTop: '1.5rem' }}>
            <h3>Pagos Registrados</h3>
            <table className="tabla-amortizacion">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Fecha de Pago</th>
                  <th>Capital</th>
                  <th>Interés</th>
                  <th>Mora</th>
                  <th>Total</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {pagosRegistrados.map((pago, index) => (
                  <tr key={pago.id}>
                    <td>{index + 1}</td>
                    <td>{formatearFechaLocal(pago.fecha_pago) || '-'}</td>
                    <td>{formatearMonto(pago.monto_capital)}</td>
                    <td>{formatearMonto(pago.monto_interes)}</td>
                    <td>{formatearMonto(pago.monto_mora)}</td>
                    <td style={{ fontWeight: 'bold' }}>
                      {formatearMonto(pago.monto_total)}
                    </td>
                    <td>{pago.tipo_pago || 'normal'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2"><strong>Total Pagado</strong></td>
                  <td>
                    <strong>
                      {formatearMonto(totalesPagos.capital)}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatearMonto(totalesPagos.interes)}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatearMonto(totalesPagos.mora)}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatearMonto(totalesPagos.total)}
                    </strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

          <div className="plan-resumen">
            <h3>Resumen del Préstamo</h3>
            <div className="resumen-grid">
              <div className="resumen-item">
                <label>Cuotas Pagadas:</label>
                <span>{prestamoSeleccionado.cuotas_pagadas || 0} de {prestamoSeleccionado.cuotas || 0}</span>
              </div>
              <div className="resumen-item">
                <label>Cuotas Pendientes:</label>
                <span>{(prestamoSeleccionado.cuotas || 0) - (prestamoSeleccionado.cuotas_pagadas || 0)}</span>
              </div>
              <div className="resumen-item">
                <label>Monto Pagado:</label>
                <span>${formatearMonto(totalesPagos.total)}</span>
              </div>
              <div className="resumen-item">
                <label>Monto Pendiente:</label>
                <span>${formatearMonto(Math.max(Number.parseFloat(prestamoSeleccionado.total || 0) - totalesPagos.total, 0))}</span>
              </div>
              <div className="resumen-item">
                <label>Pagos Registrados:</label>
                <span>{pagosRegistrados.length}</span>
              </div>
              <div className="resumen-item">
                <label>Último Pago:</label>
                <span>{pagosRegistrados.length ? (formatearFechaLocal(pagosRegistrados[pagosRegistrados.length - 1].fecha_pago) || '-') : '-'}</span>
              </div>
            </div>
          </div>

          <div className="no-print plan-acciones">
            <button className="btn-primary" onClick={imprimirPlan}>
              Imprimir Plan
            </button>
            <button className="btn-secondary" onClick={exportarPDF}>
              Exportar a PDF
            </button>
            <button className="btn-secondary" onClick={() => {
              setPrestamoSeleccionado(null);
              setPlanAmortizacion([]);
            }}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanPagos;
